import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Variable,
    upsertDepartmentVariableStep,
} from '../../../services/api/departmentVariablesService';
import { VariableSection } from '../utils/variableTypes';
import { DepartmentUser, VariableFormHandle } from './WizardVariableForm';
import VariableForm from './WizardVariableForm';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import SetupIntroduction from './SetupIntroduction';
import { EVENT_KEYS, trackLoad } from '../../../services/api/trackingService';

interface SetupWizardProps {
    savedVariables: Variable[];
    users: DepartmentUser[];
    sections: VariableSection[];
    initialStep?: number;
    onStepSaved: () => Promise<void>;
    onComplete: () => void | Promise<void>;
}

export default function SetupWizard({
    savedVariables,
    users,
    sections,
    initialStep = 0,
    onStepSaved,
    onComplete,
}: SetupWizardProps) {
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [showIntroduction, setShowIntroduction] = useState(true);
    const [advancing, setAdvancing] = useState(false);
    const [isCurrentStepValid, setIsCurrentStepValid] = useState(true);
    const formRef = useRef<VariableFormHandle>(null);

    const section = sections[currentStep];
    const totalSteps = sections.length;
    const isLastStep = currentStep === totalSteps - 1;
    const stepTitle = section?.title ?? 'Step';
    const stepDescription = section?.description ?? '';

    useEffect(() => {
        const maxStep = Math.max(totalSteps - 1, 0);
        const targetStep = Math.min(Math.max(initialStep, 0), maxStep);
        setCurrentStep(targetStep);
        setShowIntroduction(true);
    }, [initialStep, totalSteps]);

    useEffect(() => {
        if (showIntroduction) return;
        trackLoad(EVENT_KEYS.wizardStep, { step: currentStep });
    }, [currentStep, showIntroduction]);

    if (!section || sections.length === 0) {
        return null;
    }

    const handleContinue = async () => {
        setAdvancing(true);
        try {
            const saved = await formRef.current?.submit();
            if (saved !== true) return;
            if (isLastStep) {
                await onComplete();
            } else {
                setCurrentStep((s) => s + 1);
            }
        } finally {
            setAdvancing(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 0) {
            setShowIntroduction(true);
            return;
        }
        setCurrentStep((s) => s - 1);
    };

    if (showIntroduction) {
        return (
            <SetupIntroduction
                sections={sections}
                onStart={() => setShowIntroduction(false)}
            />
        );
    }

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-muted-foreground">
                            {t('departmentVariables.wizard.setupProgress')}
                        </p>
                        <p className="text-sm font-medium">{stepTitle}</p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {t('departmentVariables.wizard.stepOf', { current: currentStep + 1, total: totalSteps })}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {Array.from({ length: totalSteps }).map((_, i) => (
                        <div
                            key={i}
                            className={`h-2 flex-1 rounded-full transition-colors ${
                                i < currentStep
                                    ? 'bg-primary'
                                    : i === currentStep
                                    ? 'bg-primary/70'
                                    : 'bg-muted'
                            }`}
                        />
                    ))}
                </div>
            </div>

            <Card className="border-subtle">
                <div className="px-6 pt-6 pb-4 border-b border-subtle">
                    <h2 className="text-lg font-semibold">{stepTitle}</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                        {stepDescription}
                    </p>
                </div>

                <CardContent className="px-6 py-6">
                    <VariableForm
                        key={section.key}
                        ref={formRef}
                        defaults={section.variables}
                        saved={savedVariables}
                        users={users}
                        onSubmitValues={async (values) => {
                            const filled = Object.values(values).filter(
                                (v) => v.fixedValue?.trim() || v.refId
                            );
                            if (filled.length === 0) return;
                            await upsertDepartmentVariableStep(section.key, {
                                variables: filled,
                            });
                        }}
                        onSaved={onStepSaved}
                        onValidityChange={setIsCurrentStepValid}
                        hideFooter
                    />
                </CardContent>

                <div className="flex justify-between px-6 pb-6 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={handleBack}
                        disabled={advancing}
                    >
                        {t('departmentVariables.wizard.back')}
                    </Button>
                    <Button
                        type="button"
                        onClick={handleContinue}
                        disabled={advancing || !isCurrentStepValid}
                    >
                        {advancing
                            ? t('departmentVariables.variableRow.saving')
                            : isLastStep
                            ? t('departmentVariables.wizard.finishSetup')
                            : t('departmentVariables.wizard.saveAndContinue')}
                    </Button>
                </div>
            </Card>
        </div>
    );
}
